/**
 * Shared state for the Dev/Admin Event Override overlay.
 *
 * - `hidden`  : when true, the floating overlay (pill + panel) is not rendered.
 *               Persisted in localStorage so it stays hidden across reloads.
 * - `hide()`  : hide the overlay (used by the red "Hide" button).
 * - `reveal()`: bring the overlay back (collapsed pill).
 * - `registerIdClick()` : call this on the top-right "ID: #..." element.
 *               Clicking it 5 times within 10s reveals the overlay again.
 *
 * State is shared across components via Nuxt `useState`, so clicking the ID
 * on the home page reactively brings back the pill rendered by the layout.
 */
const HIDDEN_STORAGE_KEY = "dev.eventOverride.hidden";
const REQUIRED_CLICKS = 5;
const CLICK_WINDOW_MS = 10_000;

export const useDevOverride = () => {
  const hidden = useState<boolean>("dev-override-hidden", () => false);
  const clickTimestamps = useState<number[]>("dev-override-id-clicks", () => []);

  /** Load the persisted hidden flag from localStorage (call on mount, client only). */
  function syncFromStorage() {
    try {
      hidden.value = localStorage.getItem(HIDDEN_STORAGE_KEY) === "1";
    } catch {}
  }

  function hide() {
    hidden.value = true;
    try {
      localStorage.setItem(HIDDEN_STORAGE_KEY, "1");
    } catch {}
  }

  function reveal() {
    hidden.value = false;
    clickTimestamps.value = [];
    try {
      localStorage.removeItem(HIDDEN_STORAGE_KEY);
    } catch {}
  }

  /** Count a click on the "ID" element; reveal after 5 clicks within 10 seconds. */
  function registerIdClick() {
    if (!hidden.value) return; // already visible, nothing to do
    const now = Date.now();
    clickTimestamps.value = [...clickTimestamps.value, now].filter(
      (t) => now - t <= CLICK_WINDOW_MS
    );
    if (clickTimestamps.value.length >= REQUIRED_CLICKS) {
      reveal();
    }
  }

  return { hidden, hide, reveal, registerIdClick, syncFromStorage };
};
